import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import apiClient from '../api/client';
import { useTheme } from '../contexts/ThemeContext';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Download, Calendar, Users, TrendingUp } from 'lucide-react';

const Reports = () => {
  const { isDarkMode } = useTheme();
  const [selectedMonth, setSelectedMonth] = useState('');
  const [attendanceReport, setAttendanceReport] = useState(null);
  const [payrollReport, setPayrollReport] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const currentDate = new Date();
    const monthStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    setSelectedMonth(monthStr);
    fetchReports(monthStr);
  }, []);

  const fetchReports = async (month) => {
    try {
      const [attRes, payRes] = await Promise.all([
        apiClient.get(`/api/reports/attendance?month=${month}`),
        apiClient.get(`/api/reports/payroll?month=${month}`).catch(() => ({data: []}))
      ]);
      setAttendanceReport(attRes.data);
      setPayrollReport(payRes.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPayroll = async () => {
    try {
      const response = await apiClient.get(`/api/reports/export/payroll?month=${selectedMonth}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll_report_${selectedMonth}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting report:', error);
    }
  };

  // Prepare attendance chart data
  const prepareAttendanceChartData = () => {
    if (!attendanceReport) return [];
    
    const data = Object.values(attendanceReport).map(emp => ({
      name: emp.emp_name?.substring(0, 15) || emp.emp_code,
      Present: emp.present || 0,
      Absent: emp.absent || 0,
      'Half Day': emp.half_day || 0,
      Leave: emp.leave || 0
    }));
    
    return data.slice(0, 20); // Show top 20 employees
  };

  // Prepare attendance summary for pie chart
  const prepareAttendanceSummaryData = () => {
    if (!attendanceReport) return [];
    
    let totalPresent = 0;
    let totalAbsent = 0;
    let totalHalfDay = 0;
    let totalLeave = 0;
    
    Object.values(attendanceReport).forEach(emp => {
      totalPresent += emp.present || 0;
      totalAbsent += emp.absent || 0;
      totalHalfDay += emp.half_day || 0;
      totalLeave += emp.leave || 0;
    });
    
    return [
      { name: 'Present', value: totalPresent, color: '#10B981' },
      { name: 'Absent', value: totalAbsent, color: '#EF4444' },
      { name: 'Half Day', value: totalHalfDay, color: '#F59E0B' },
      { name: 'Leave', value: totalLeave, color: '#0080FF' }
    ];
  };

  // Prepare payroll chart data
  const preparePayrollChartData = () => {
    return payrollReport.slice(0, 15).map(record => ({
      name: record.employee_name?.substring(0, 15) || record.emp_code,
      'Gross': record.gross_salary || 0,
      'Net': record.net_salary || 0,
      'Deductions': record.total_deductions || 0
    }));
  };

  if (loading) {
    return (
      <Layout>
        <div className='flex items-center justify-center h-full'>
          <div className='loading-spinner'></div>
        </div>
      </Layout>
    );
  }

  const attendanceChartData = prepareAttendanceChartData();
  const attendanceSummaryData = prepareAttendanceSummaryData();
  const payrollChartData = preparePayrollChartData();

  return (
    <Layout>
      <div className='page-header flex items-center justify-between'>
        <div>
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900'}`}>Reports & Analytics</h1>
          <p className={`mt-1 ${isDarkMode ? 'text-[hsl(215_10%_70%)]' : 'text-gray-600'}`}>View insights and export reports</p>
        </div>
        <button
          onClick={handleExportPayroll}
          className='flex items-center gap-2 bg-[#00B8B8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#0ED2D2]'
        >
          <Download size={20} />
          Export Payroll (Excel)
        </button>
      </div>

      <div className={`rounded-xl border p-4 mb-6 ${isDarkMode ? 'bg-[hsl(220_6%_8%)] border-[hsl(215_10%_22%)]' : 'bg-white border-gray-200'}`}>
        <div className='flex items-center gap-4'>
          <label className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900'}`}>Select Month:</label>
          <input
            type='month'
            value={selectedMonth}
            onChange={(e) => {
              setSelectedMonth(e.target.value);
              fetchReports(e.target.value);
            }}
            className={`px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#00B8B8] ${isDarkMode ? 'bg-[hsl(220_6%_12%)] border-[hsl(215_10%_22%)] text-[hsl(210_15%_92%)]' : 'bg-white border-gray-200 text-gray-900'}`}
          />
        </div>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6'>
        {/* Attendance Summary Pie Chart */}
        <div className={`rounded-xl border p-6 ${isDarkMode ? 'bg-[hsl(220_6%_8%)] border-[hsl(215_10%_22%)]' : 'bg-white border-gray-200'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900'}`}>Attendance Summary</h3>
          {attendanceSummaryData.length > 0 ? (
            <ResponsiveContainer width='100%' height={300}>
              <PieChart>
                <Pie
                  data={attendanceSummaryData}
                  cx='50%'
                  cy='50%'
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill='#8884d8'
                  dataKey='value'
                >
                  {attendanceSummaryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className='text-center py-12 text-[#737373]'>No attendance data for selected month</div>
          )}
        </div>

        {/* Attendance Stats */}
        <div className='bg-white rounded-xl border border-[#E5E5E5] p-6'>
          <h3 className='text-lg font-semibold text-[#262626] mb-4'>Attendance Statistics</h3>
          <div className='space-y-3'>
            {attendanceSummaryData.map((item, idx) => (
              <div key={idx} className='flex items-center justify-between p-3 bg-[#F5F5F5] rounded-lg'>
                <div className='flex items-center gap-3'>
                  <div className='w-4 h-4 rounded-full' style={{backgroundColor: item.color}}></div>
                  <span className='font-medium text-[#262626]'>{item.name}</span>
                </div>
                <span className='text-2xl font-bold text-[#262626]'>{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Employee-wise Attendance Chart */}
      {attendanceChartData.length > 0 && (
        <div className='bg-white rounded-xl border border-[#E5E5E5] p-6 mb-6'>
          <h3 className='text-lg font-semibold text-[#262626] mb-4'>Employee-wise Attendance (Top 20)</h3>
          <ResponsiveContainer width='100%' height={400}>
            <BarChart data={attendanceChartData}>
              <CartesianGrid strokeDasharray='3 3' />
              <XAxis dataKey='name' angle={-45} textAnchor='end' height={100} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey='Present' fill='#10B981' />
              <Bar dataKey='Absent' fill='#EF4444' />
              <Bar dataKey='Half Day' fill='#F59E0B' />
              <Bar dataKey='Leave' fill='#0080FF' />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Payroll Summary Chart */}
      {payrollChartData.length > 0 && (
        <div className='bg-white rounded-xl border border-[#E5E5E5] p-6'>
          <h3 className='text-lg font-semibold text-[#262626] mb-4'>Payroll Summary (Top 15)</h3>
          <ResponsiveContainer width='100%' height={400}>
            <BarChart data={payrollChartData}>
              <CartesianGrid strokeDasharray='3 3' />
              <XAxis dataKey='name' angle={-45} textAnchor='end' height={100} />
              <YAxis />
              <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
              <Legend />
              <Bar dataKey='Gross' fill='#7EC845' />
              <Bar dataKey='Deductions' fill='#EF4444' />
              <Bar dataKey='Net' fill='#1B5E4A' />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Layout>
  );
};

export default Reports;